import React, { FunctionComponent, useCallback, useMemo, useRef, type JSX } from 'react';
import { useDropzone } from 'react-dropzone';

import { Tooltip } from '@mui/material';
import FieldLabel from '../fieldLabel/FieldLabel';

import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import './FileUploadField.sass';

type Props = {
  name: string;
  value?: FileList;
  label?: string;
  description?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  allowMultipleUploads?: boolean;
  acceptFileTypes?: string[];
  onChange?: (value: { [key: string]: FileList | null }) => void;
  onTouch?: (fieldName: string) => void;
  enableDragAndDrop?: boolean;
  uploadTriggerIcon?: TIconName;
};

const FileUploadField: FunctionComponent<React.PropsWithChildren<Props>> = ({
  name,
  value,
  label,
  description,
  isDisabled = false,
  isRequired = false,
  acceptFileTypes,
  allowMultipleUploads = false,
  onChange,
  onTouch,
  enableDragAndDrop = false,
  uploadTriggerIcon,
}: React.PropsWithChildren<Props>): JSX.Element => {
  const inputElementRef = useRef<HTMLInputElement>(null);

  const onUploadTriggerClick = useCallback(() => {
    inputElementRef.current?.click();
  }, [inputElementRef]);

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.({
        [event.target.name]: event.target.files,
      });
      onTouch?.(event.target.name);
      // Allow uploading of the same file
      if (event.target && event.target.value) {
        event.target.value = '';
      }
    },
    [onChange, onTouch]
  );

  const fileListElement = useMemo(() => {
    if (!value) return;
    const fileArray = Array.prototype.slice.call(value);

    return (
      <ul>
        {fileArray.map((file: File, index: number) => {
          return <li key={index}>{file.name}</li>;
        })}
      </ul>
    );
  }, [value]);

  const onDrop = useCallback(
    (files: File[]) => {
      const filesToUpload: any = files.slice(
        0,
        allowMultipleUploads ? files.length : 1
      );
      onChange?.({
        [name]: filesToUpload,
      });
      onTouch?.(name);
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'name'
    [allowMultipleUploads, onChange, onTouch]
  );

  const acceptedFiles = useMemo(
    () => acceptFileTypes?.join(','),
    [acceptFileTypes]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    disabled: isDisabled,
    onDrop,
    accept: acceptedFiles,
  });

  return (
    <div className={`file-upload-field`}>
      <FieldLabel
        label={label}
        isRequired={isRequired}
        description={description}
      />
      <input
        ref={inputElementRef}
        type="file"
        style={{ display: 'none' }}
        onChange={onInputChange}
        multiple={allowMultipleUploads}
        name={name}
        accept={acceptedFiles}
        disabled={isDisabled}
        {...(enableDragAndDrop ? getInputProps() : undefined)}
      />
      <Tooltip
        title={
          acceptedFiles?.length ? (
            <small>
              Accepted file types: {acceptedFiles?.replaceAll(',', ', ')}
            </small>
          ) : (
            ''
          )
        }
        arrow
        placement="bottom"
        PopperProps={{
          className: `MuiTooltip-popper MuiTooltip-popperArrow`,
        }}
      >
        <div className={`file-block`}>
          <div
            className={`file-upload-trigger main-block ${
              isDragActive ? 'dragging' : ''
            }${isDisabled ? 'disabled' : ''}`}
            onClick={!isDisabled ? onUploadTriggerClick : undefined}
            {...(enableDragAndDrop ? getRootProps() : undefined)}
          >
            <UntitledIcon name={uploadTriggerIcon || 'plus.3'} />
          </div>
        </div>
      </Tooltip>
      <div className="file-list-container">{fileListElement}</div>
    </div>
  );
};

export default FileUploadField;
